import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { GET_WORK_ORDERS } from '../graphql/queries';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import WorkOrderDetailSlidePanel from '../components/work-orders/WorkOrderDetailSlidePanel';

const ITEMS_PER_PAGE = 20;

const AREA_GROUPS = [
  {
    value: 'kiawah_seabrook',
    label: 'Kiawah + Seabrook',
    areas: ['KIAWAH', 'SEABROOK_ISLAND']
  },
  {
    value: 'iop_wild_dunes',
    label: 'Isle of Palms + Wild Dunes',
    areas: ['ISLE_OF_PALMS', 'ISLE_OF_PALMS_WILD_DUNES']
  }
];

const DEPARTMENT_OPTIONS = [
  {
    value: 'property_management',
    label: 'Property Management',
    roles: ['PROPERTY_MANAGER', 'MAINTENANCE']
  },
  {
    value: 'housekeeping',
    label: 'Housekeeping',
    roles: ['HOUSEKEEPER_MANAGER']
  }
];

export default function PastWorkOrders() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(null);
  const [isSlidePanelOpen, setIsSlidePanelOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const getSelectedAreas = () => {
    if (!areaFilter) return undefined;
    const group = AREA_GROUPS.find(g => g.value === areaFilter);
    return group?.areas;
  };

  const getSelectedRoles = () => {
    if (!departmentFilter) return undefined;
    const dept = DEPARTMENT_OPTIONS.find(d => d.value === departmentFilter);
    return dept?.roles;
  };

  const getQueryStatuses = () => {
    if (showDeleted) return undefined;
    if (statusFilter) return [statusFilter];
    return ['COMPLETED', 'CANCELED'];
  };

  const { loading, error, data, refetch } = useQuery(GET_WORK_ORDERS, {
    variables: {
      input: {
        limit: ITEMS_PER_PAGE,
        page: currentPage,
        search: searchTerm || undefined,
        statuses: getQueryStatuses(),
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        propertyId: propertyFilter || undefined,
        areas: getSelectedAreas(),
        assignedRoles: getSelectedRoles(),
        includeDeleted: showDeleted || undefined
      }
    },
    fetchPolicy: 'network-only',
  });

  const workOrders = data?.workOrders?.items || [];
  const totalResults = data?.workOrders?.total || 0;
  
  useEffect(() => {
    setTotalPages(Math.ceil(totalResults / ITEMS_PER_PAGE));
  }, [totalResults]);
  
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when changing filters
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setPropertyFilter('');
    setAreaFilter('');
    setDepartmentFilter('');
    setShowDeleted(false);
    setCurrentPage(1);
    refetch({
      input: {
        limit: ITEMS_PER_PAGE,
        page: 1,
        statuses: ['COMPLETED', 'CANCELED']
      }
    });
  };

  const handleOpenSlidePanel = (workOrderId) => {
    setSelectedWorkOrderId(workOrderId);
    setIsSlidePanelOpen(true);
  };

  const handleCloseSlidePanel = useCallback(() => {
    setIsSlidePanelOpen(false);
    setSelectedWorkOrderId(null);
  }, []);

  const handleWorkOrderDataChange = useCallback(() => {
    refetch();
  }, [refetch]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">
          {showDeleted ? 'Deleted Work Orders' : 'Past Work Orders'}
        </h1>
        <Link 
          to="/dashboard/work-orders" 
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Active Work Orders
        </Link>
      </div>

      {/* Filters - Accordion */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          className="w-full px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
          aria-expanded={isFiltersOpen}
          aria-controls="past-work-orders-filters"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-800">Filters</h3>
              {(searchTerm || statusFilter || dateFrom || dateTo || areaFilter || departmentFilter || showDeleted) && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {[searchTerm, statusFilter, dateFrom, dateTo, areaFilter, departmentFilter, showDeleted].filter(Boolean).length} active
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {(searchTerm || statusFilter || dateFrom || dateTo || areaFilter || departmentFilter || showDeleted) && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); handleClearFilters(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleClearFilters(); } }}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label="Reset all filters"
                >
                  Reset all
                </span>
              )}
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${isFiltersOpen ? 'rotate-180' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        </button>

        {isFiltersOpen && (
          <form id="past-work-orders-filters" onSubmit={handleSearch} className="p-5 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4">
              {/* Search */}
              <div className="md:col-span-3">
                <label htmlFor="search" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Search
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                    placeholder="Search by description or property name..."
                  />
                </div>
              </div>

              {/* Area */}
              <div>
                <label htmlFor="areaFilter" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Area
                </label>
                <select
                  id="areaFilter"
                  value={areaFilter}
                  onChange={(e) => { setAreaFilter(e.target.value); setCurrentPage(1); }}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                >
                  <option value="">All Areas</option>
                  {AREA_GROUPS.map(group => (
                    <option key={group.value} value={group.value}>{group.label}</option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div>
                <label htmlFor="departmentFilter" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Department
                </label>
                <select
                  id="departmentFilter"
                  value={departmentFilter}
                  onChange={(e) => { setDepartmentFilter(e.target.value); setCurrentPage(1); }}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                >
                  <option value="">All Departments</option>
                  {DEPARTMENT_OPTIONS.map(dept => (
                    <option key={dept.value} value={dept.value}>{dept.label}</option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Status
                </label>
                <select
                  id="status"
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                >
                  <option value="">All Statuses</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELED">Canceled</option>
                </select>
              </div>

              {/* Show Deleted Toggle */}
              <div className="flex items-end pb-1">
                <label
                  className="relative inline-flex items-center cursor-pointer"
                  htmlFor="show-deleted-toggle"
                >
                  <input
                    type="checkbox"
                    id="show-deleted-toggle"
                    checked={showDeleted}
                    onChange={() => {
                      setShowDeleted(!showDeleted);
                      setCurrentPage(1);
                    }}
                    className="sr-only peer"
                    aria-label="Toggle showing deleted work orders"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Show Deleted
                  </span>
                </label>
              </div>

              {/* Date Range */}
              <div>
                <label htmlFor="dateFrom" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  From Date
                </label>
                <input
                  type="date"
                  id="dateFrom"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="dateTo" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  To Date
                </label>
                <input
                  type="date"
                  id="dateTo"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full py-2.5 px-3 rounded-lg border border-gray-300 bg-gray-50 text-sm text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-end">
                <div className="flex space-x-2 w-full">
                  <button
                    type="submit"
                    className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm"
                  >
                    <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Work Orders List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">{showDeleted ? 'Loading deleted work orders...' : 'Loading past work orders...'}</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">
            <p>Error loading work orders: {error.message}</p>
          </div>
        ) : !workOrders.length ? (
          <div className="p-6 text-center text-gray-500">
            <p>{showDeleted ? 'No deleted work orders found.' : 'No past work orders found.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {showDeleted ? 'Deleted' : 'Date'}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {showDeleted ? 'Deleted By' : 'Submitted By'}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {workOrders.map((workOrder) => (
                  <tr 
                    key={workOrder.id} 
                    className={`hover:bg-gray-50 ${showDeleted ? 'bg-red-50/30' : workOrder.isUrgent ? 'bg-red-50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{workOrder.property?.name}</div>
                      <div className="text-sm text-gray-500">{workOrder.property?.address}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        {workOrder.isUrgent && (
                          <span className="inline-flex items-center px-2.5 py-0.5 mr-2 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Urgent
                          </span>
                        )}
                        {workOrder.streamlineWorkOrderId && (
                          <span className="inline-flex items-center px-2.5 py-0.5 mr-2 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Streamline
                          </span>
                        )}
                        <div className="text-sm text-gray-900 line-clamp-2">{workOrder.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {showDeleted && workOrder.deletedAt
                        ? format(new Date(workOrder.deletedAt), 'MMM d, yyyy')
                        : format(new Date(workOrder.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {showDeleted ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          DELETED
                        </span>
                      ) : (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(workOrder.status)}`}>
                          {workOrder.status.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {showDeleted
                        ? (workOrder.deletedBy ? `${workOrder.deletedBy.firstName} ${workOrder.deletedBy.lastName}` : '—')
                        : (workOrder.submitterName || workOrder.submittedBy?.name || 'Unknown')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={() => handleOpenSlidePanel(workOrder.id)}
                          className="inline-flex items-center px-3 py-1 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 cursor-pointer"
                          aria-label={`View work order for ${workOrder.property?.name}`}
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {totalResults > 0 && (
        <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow">
          <div>
            Showing <span className="font-medium">{Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalResults)}</span> to{' '}
            <span className="font-medium">
              {Math.min(currentPage * ITEMS_PER_PAGE, totalResults)}
            </span>{' '}
            of <span className="font-medium">{totalResults}</span> results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                // Calculate which page numbers to show
                if (totalPages <= 5) {
                  // Less than 5 pages, show all
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  // Near the start
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // Near the end
                  pageNumber = totalPages - 4 + i;
                } else {
                  // In the middle
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === pageNumber
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-md ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Work Order Detail Slide Panel */}
      <WorkOrderDetailSlidePanel
        isOpen={isSlidePanelOpen}
        onClose={handleCloseSlidePanel}
        workOrderId={selectedWorkOrderId}
        onDataChange={handleWorkOrderDataChange}
      />
    </div>
  );
} 